import { CollectionResponse } from "@rumble-it/common-lib/types/api/CollectionResponse.interface";
import { axiosInstance } from "../client";

/**
 * Read model for a resolved set of inference options, mirroring the backend InferenceOptionsOutput.
 * Exposed to the admin with camelCase field names (the provider-facing snake_case wire names are
 * reserved for talking to the platform).
 */
export interface InferenceOptionsOutput {
  temperature?: number;
  maxTokens?: number;
  topP?: number;
}

/**
 * Read-safe decomposition of an AI Profile's resolved connection (provider, model, host, port,
 * effective inference options). Mirrors the backend AiConnectionOutput: it exposes which
 * provider/model a connection uses without ever leaking the API key.
 */
export interface AiConnection {
  provider: string;
  providerLabel: string;
  model: string;
  host: string;
  port: number | null;
  options: InferenceOptionsOutput | null;
  profileName?: string | null;
}

/**
 * Mirrors the backend AgentOutput. The API never returns the API key; only the read-safe
 * `aiConnection` decomposition is exposed on read.
 */
export interface Agent {
  id: string;
  name: string;
  instructions: string;
  allowedTools: string[];
  hasVision: boolean;
  responseFormatSchema: Record<string, unknown> | null;
  maxTokensPerSession: number | null;
  // UUID of the AiProfile that provides this agent's connection.
  aiProfileId: string | null;
  overrideInferenceOptions: InferenceOptionsOutput | null;
  aiConnection: AiConnection | null;
}

/**
 * Mirrors the backend AgentInput. Only these fields are accepted on write; the connection itself
 * lives on the referenced AiProfile (see `aiProfile`).
 *
 * `maxTokensPerSession` is a cumulative token budget for the whole session (enforced across every
 * turn), which is distinct from the per-request output cap. The per-request inference overrides
 * (`overrideTemperature`, `overrideMaxTokens`, `overrideTopP`) override the AI profile's defaults for
 * this agent; leave them null/undefined to inherit the profile.
 */
export interface AgentInput {
  name: string;
  // UUID of the AiProfile (required).
  aiProfile: string;
  instructions: string;
  allowedTools: string[];
  responseFormatSchema?: Record<string, unknown> | null;
  // Cumulative token budget for the whole session (not a per-request cap).
  maxTokensPerSession?: number | null;
  // Per-agent inference-options override (per-request); null/undefined inherits the profile default.
  overrideTemperature?: number | null;
  overrideMaxTokens?: number | null;
  overrideTopP?: number | null;
}

/**
 * Mirrors the backend AiProfileOutput. The API key is NEVER returned on read for security; use
 * `aiConnection` for the read-safe provider/model view.
 */
export interface AiProfile {
  id: string;
  name: string;
  hasVision: boolean;
  contextWindowInput: number | null;
  contextWindowOutput: number | null;
  capabilities: string[] | null;
  aiConnection: AiConnection | null;
}

/**
 * Mirrors the backend AiProfileInput. Connection details are structured fields. `apiKey` is
 * write-only and carries the plaintext key: null/empty means
 * inherit the platform default key for the provider. `host`/`port` are only meaningful for
 * self-hosted/custom endpoints (e.g. Ollama); null/empty means the provider's standard endpoint.
 * `capabilities`/`contextWindowInput`/`contextWindowOutput` are model-intrinsic and are no longer
 * accepted on write; use `AiProfile.aiConnection`/read fields to display them.
 */
export interface AiProfileInput {
  name: string;
  provider: string;
  model: string;
  apiKey?: string | null;
  host?: string | null;
  port?: number | null;
  temperature?: number | null;
  maxTokens?: number | null;
  topP?: number | null;
}

/**
 * Mirrors the backend AgentCollectionOutput (the Agent list / GetCollection read model). Slimmer
 * than {@link Agent}: it exposes only what the list needs - the agent name and a compact view of its
 * AI profile (profile name, provider label and model name) - and deliberately omits the computed
 * `aiConnection` decomposition, which is only resolved on the single-item endpoint.
 */
export interface AgentListItem {
  id: string;
  name: string;
  // UUID of the AiProfile that provides this agent's connection.
  aiProfileId: string;
  profileName: string;
  // Human-readable provider label, or null when the profile has no model configured.
  providerLabel: string | null;
  // Model name, or null when the profile has no model configured.
  model: string | null;
}

export interface AgentListParams {
  name?: string;
  // Property to sort on, e.g. "name" or "profileName".
  orderField?: "name" | "profileName";
  // Sort direction for the chosen field, e.g. "asc" | "desc".
  orderDirection?: "asc" | "desc";
  aiProfileId?: string;
  // 1-based page number for server-side pagination.
  page?: number;
}

export const fetchAgentsList = async (
  params: AgentListParams = {},
): Promise<CollectionResponse<AgentListItem>> => {
  const search = new URLSearchParams({ page: String(params.page ?? 1) });
  if (params.name) {
    search.set("name", params.name);
  }
  if (params.orderField && params.orderDirection) {
    search.set(`order[${params.orderField}]`, params.orderDirection);
  }
  if (params.aiProfileId) {
    search.set("aiProfileId", params.aiProfileId);
  }
  return axiosInstance
    .get(`/agents?${search.toString()}`)
    .then((response) => response.data);
};

export const fetchAgent = async (id: string): Promise<Agent> => {
  return axiosInstance.get(`/agents/${id}`).then((response) => response.data);
};

export const createAgent = async (data: AgentInput): Promise<Agent> => {
  return axiosInstance.post("/agents", data).then((response) => response.data);
};

export const updateAgent = async (
  id: string,
  data: Partial<AgentInput>,
): Promise<Agent> => {
  return axiosInstance
    .patch(`/agents/${id}`, data, {
      headers: {
        "Content-Type": "application/merge-patch+json",
      },
    })
    .then((response) => response.data);
};

export const deleteAgent = async (id: string): Promise<void> => {
  return axiosInstance
    .delete(`/agents/${id}`)
    .then((response) => response.data);
};

export const fetchAiProfiles = async (): Promise<
  CollectionResponse<AiProfile>
> => {
  return axiosInstance.get(`/ai-profiles`).then((response) => response.data);
};

export const fetchAiProfile = async (id: string): Promise<AiProfile> => {
  return axiosInstance
    .get(`/ai-profiles/${id}`)
    .then((response) => response.data);
};

export const createAiProfile = async (
  data: AiProfileInput,
): Promise<AiProfile> => {
  return axiosInstance
    .post("/ai-profiles", data)
    .then((response) => response.data);
};

export const updateAiProfile = async (
  id: string,
  data: Partial<AiProfileInput>,
): Promise<AiProfile> => {
  return axiosInstance
    .patch(`/ai-profiles/${id}`, data, {
      headers: { "Content-Type": "application/merge-patch+json" },
    })
    .then((response) => response.data);
};

export const deleteAiProfile = async (id: string): Promise<void> => {
  return axiosInstance
    .delete(`/ai-profiles/${id}`)
    .then((response) => response.data);
};

export interface AiConnectionTestResult {
  success: boolean;
  message: string;
}

export const testAiProfile = async (
  data: AiProfileInput,
): Promise<AiConnectionTestResult> => {
  return axiosInstance
    .post("/ai-profiles/test", data)
    .then((response) => response.data);
};

export interface SyntheticEmailAttachment {
  filename: string;
  mimeType: string;
}

export interface SyntheticEmailScenario {
  senderName: string;
  senderEmail: string;
  subject: string;
  bodyText: string;
  date?: string | null;
  attachments: SyntheticEmailAttachment[];
}

export interface SyntheticEmailBatchRequest {
  profileId: string;
  // The backend only accepts scalar inference-option values (see SyntheticEmailBatchRequest::$options).
  options?: Record<string, string | number | boolean> | null;
  count: number;
  attributes: string[];
}

export interface SyntheticEmailBatchResponse {
  scenarios: SyntheticEmailScenario[];
  success: boolean;
  errorMessage?: string;
}

export const executeSyntheticEmailBatch = async (
  data: SyntheticEmailBatchRequest,
): Promise<SyntheticEmailBatchResponse> => {
  return axiosInstance
    .post("/ai-profiles/synthetic-emails", data)
    .then((response) => response.data);
};
